import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';


const ChurnRiskMonitor = ({ churnRiskMembers, onRefresh }) => {
  const [selectedMember, setSelectedMember] = useState(null);
  const [filterRiskLevel, setFilterRiskLevel] = useState('all');

  const riskLevels = [
    { value: 'all', label: 'All Risk Levels', color: 'gray' },
    { value: 'critical', label: 'Critical', color: 'red' },
    { value: 'high', label: 'High', color: 'orange' },
    { value: 'medium', label: 'Medium', color: 'yellow' },
    { value: 'low', label: 'Low', color: 'green' }
  ];

  const filteredMembers = churnRiskMembers?.filter(member => 
    filterRiskLevel === 'all' || member?.risk_level === filterRiskLevel
  );

  const getRiskColor = (level) => {
    const colors = {
      critical: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300' },
      high: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300' },
      medium: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300' },
      low: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300' }
    };
    return colors?.[level] || colors?.low;
  };

  const riskStats = {
    critical: churnRiskMembers?.filter(m => m?.risk_level === 'critical')?.length || 0,
    high: churnRiskMembers?.filter(m => m?.risk_level === 'high')?.length || 0,
    medium: churnRiskMembers?.filter(m => m?.risk_level === 'medium')?.length || 0,
    low: churnRiskMembers?.filter(m => m?.risk_level === 'low')?.length || 0
  };

  const handleTriggerIntervention = async (memberId) => {
    if (!confirm('Trigger churn prevention intervention for this member?')) return;

    // In production, this would trigger a churn prevention campaign
    alert('Intervention triggered - member will receive personalized outreach');
    onRefresh?.();
  };

  return (
    <div className="space-y-6">
      {/* Risk Level Distribution */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-red-50 rounded-lg border border-red-200 p-4">
          <div className="flex items-center gap-3">
            <Icon name="AlertOctagon" className="w-5 h-5 text-red-600" />
            <div>
              <p className="text-sm text-red-600">Critical Risk</p>
              <p className="text-2xl font-bold text-red-900">{riskStats?.critical}</p>
            </div>
          </div>
        </div>

        <div className="bg-orange-50 rounded-lg border border-orange-200 p-4">
          <div className="flex items-center gap-3">
            <Icon name="AlertTriangle" className="w-5 h-5 text-orange-600" />
            <div>
              <p className="text-sm text-orange-600">High Risk</p>
              <p className="text-2xl font-bold text-orange-900">{riskStats?.high}</p>
            </div>
          </div>
        </div>

        <div className="bg-yellow-50 rounded-lg border border-yellow-200 p-4">
          <div className="flex items-center gap-3">
            <Icon name="AlertCircle" className="w-5 h-5 text-yellow-600" />
            <div>
              <p className="text-sm text-yellow-600">Medium Risk</p>
              <p className="text-2xl font-bold text-yellow-900">{riskStats?.medium}</p>
            </div>
          </div>
        </div>

        <div className="bg-green-50 rounded-lg border border-green-200 p-4">
          <div className="flex items-center gap-3">
            <Icon name="CheckCircle" className="w-5 h-5 text-green-600" />
            <div>
              <p className="text-sm text-green-600">Low Risk</p>
              <p className="text-2xl font-bold text-green-900">{riskStats?.low}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center justify-between">
        <div>
          <label className="text-sm font-medium text-gray-700 mb-1 block">Filter by Risk Level</label>
          <select
            value={filterRiskLevel}
            onChange={(e) => setFilterRiskLevel(e?.target?.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            {riskLevels?.map(level => (
              <option key={level?.value} value={level?.value}>{level?.label}</option>
            ))}
          </select>
        </div>
        <div className="text-sm text-gray-600">
          Showing {filteredMembers?.length} members
        </div>
      </div>

      {/* At-Risk Members List */}
      <div className="space-y-4">
        {filteredMembers?.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <Icon name="Users" className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">No members found with selected risk level</p>
          </div>
        ) : (
          filteredMembers?.map((member) => {
            const riskColor = getRiskColor(member?.risk_level);

            return (
              <div
                key={member?.id}
                className={`border rounded-lg p-4 ${riskColor?.border} bg-white`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {member?.user?.full_name || 'Unknown Member'}
                      </h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${riskColor?.bg} ${riskColor?.text}`}>
                        {member?.risk_level?.toUpperCase()} RISK
                      </span>
                      <span className="text-sm text-gray-600">
                        Score: {member?.risk_score?.toFixed(1)}/100
                      </span>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <p className="text-sm text-gray-600">Payment Failures</p>
                        <p className="font-semibold text-gray-900">{member?.payment_failure_count}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Last Failure</p>
                        <p className="font-semibold text-gray-900">
                          {member?.last_payment_failure_date 
                            ? new Date(member?.last_payment_failure_date)?.toLocaleDateString()
                            : 'N/A'
                          }
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Engagement Score</p>
                        <p className="font-semibold text-gray-900">{member?.engagement_score?.toFixed(1)}/10</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Lifetime Value</p>
                        <p className="font-semibold text-gray-900">${member?.lifetime_value?.toFixed(2)}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <p className="text-sm text-gray-600">Last Visit</p>
                        <p className="font-semibold text-gray-900">
                          {member?.last_visit_date 
                            ? new Date(member?.last_visit_date)?.toLocaleDateString()
                            : 'No visits'
                          }
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Visit Frequency</p>
                        <p className="font-semibold text-gray-900">{member?.visit_frequency?.toFixed(1)} visits/week</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Membership Duration</p>
                        <p className="font-semibold text-gray-900">{member?.membership_duration_months} months</p>
                      </div>
                    </div>

                    {/* Risk Factors */}
                    {member?.risk_factors && (
                      <div className="mb-3">
                        <p className="text-sm font-medium text-gray-700 mb-2">Risk Factors:</p>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(member?.risk_factors || {})?.map(([key, value]) => (
                            <span key={key} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                              {key?.replace('_', ' ')}: {value?.toString()}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Recommended Actions */}
                    {member?.recommended_actions && (
                      <div className="bg-blue-50 rounded-lg p-3">
                        <p className="text-sm font-medium text-blue-900 mb-2">Recommended Actions:</p>
                        <ul className="text-sm text-blue-800 space-y-1">
                          {Object.values(member?.recommended_actions || {})?.map((action, index) => (
                            <li key={index} className="flex items-start gap-2">
                              <Icon name="ArrowRight" size={14} className="mt-0.5" />
                              <span>{action}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-2 ml-4">
                    <Button
                      size="sm"
                      iconName="Shield"
                      onClick={() => handleTriggerIntervention(member?.user_id)}
                    >
                      Trigger Intervention
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      iconName="Phone"
                    >
                      Call Member
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      iconName="MessageSquare"
                    >
                      Send Message
                    </Button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Risk Scoring Info */}
      <div className="bg-purple-50 rounded-lg border border-purple-200 p-4">
        <h4 className="font-semibold text-purple-900 mb-2 flex items-center gap-2">
          <Icon name="Info" className="w-5 h-5" />
          Member Risk Scoring
        </h4>
        <div className="text-sm text-purple-800 space-y-1">
          <p>• <strong>Payment Patterns:</strong> Number of failed payments and payment history (0-40 points)</p>
          <p>• <strong>Engagement Levels:</strong> Visit frequency and last visit date (0-30 points)</p>
          <p>• <strong>Membership Duration:</strong> Length of membership and loyalty indicators (0-30 points)</p>
          <p>• <strong>Risk Levels:</strong> Critical (75+), High (50-74), Medium (25-49), Low (0-24)</p>
        </div>
      </div>
    </div>
  );
};

export default ChurnRiskMonitor;